import type { ContinuidadIntegrante, GrupoRearmado } from '@lib/services/bajas.service';

/**
 * El reparto del grupo cuando se da de baja a su titular.
 *
 * La pantalla pregunta de a UNA persona ("¿con quién queda?") y esto es el
 * estado que se va armando con esas respuestas. Vive acá y no en el componente
 * porque es lógica pura: se testea sin renderizar nada, y es la que decide si el
 * reparto está completo — la validación de verdad la vuelve a hacer el server,
 * que es el único que no se puede saltear.
 */

/** Quiénes encabezan, con quién queda cada uno y quién se va. */
export interface EstadoReparto {
  /** afiIds de los que quedan a cargo, EN ORDEN: el primero es el grupo principal. */
  titulares: string[];
  /** afiId del integrante → afiId del titular con el que queda. */
  asignados: Record<string, string>;
  /**
   * Los que no siguen con nadie: se dan de baja junto con el titular.
   *
   * Es la tercera salida de cada tarjeta, y la única para el adherente o el
   * nieto — que no pueden encabezar un grupo y a los que nadie tiene por qué
   * llevarse.
   */
  bajas: string[];
}

export const REPARTO_VACIO: EstadoReparto = { titulares: [], asignados: {}, bajas: [] };

/**
 * Requisitos que HOY frenan el rearmado desde el portal.
 *
 * El domicilio no está: cuando el promovido no tiene el suyo, el server le copia
 * el del saliente (es la misma casa). El tutor y el curador sí frenan hasta que
 * `MODIF_RESP_TUT` esté expuesto — dejarlos pasar promovería a un menor sin
 * responsable.
 */
const REQUISITOS_QUE_FRENAN = ['tutor', 'curador'] as const;

/** Lo que falta cargarle y todavía no se puede cargar desde acá. */
export function requisitosPendientes(i: ContinuidadIntegrante): string[] {
  return (i.requisitos ?? []).filter((r) =>
    (REQUISITOS_QUE_FRENAN as readonly string[]).includes(r),
  );
}

/** ¿Se lo puede dejar a cargo de un grupo desde el portal? */
export function puedeQuedarACargo(i: ContinuidadIntegrante): boolean {
  return i.elegible && requisitosPendientes(i).length === 0;
}

/**
 * Por qué NO puede quedar a cargo, en el idioma del usuario.
 *
 * Nunca devuelve vacío para alguien que no puede: la persona se muestra igual
 * en la lista con el motivo al lado. Esconderla hace que el operador crea que
 * falta gente del grupo.
 */
export function motivoNoPuede(i: ContinuidadIntegrante): string {
  if (puedeQuedarACargo(i)) return '';
  const pendientes = requisitosPendientes(i);
  if (i.elegible && pendientes.length) {
    return `Hay que consignarle ${pendientes.join(' y ')}, y eso todavía se hace por el operador.`;
  }
  return i.detalle || 'No puede quedar a cargo del grupo.';
}

/** Una rama del reparto tal como va quedando. */
export interface Rama {
  titular: ContinuidadIntegrante;
  miembros: ContinuidadIntegrante[];
  /** La que se queda con el legajo original. Las demás se llevan uno nuevo. */
  esPrincipal: boolean;
}

export function ramas(
  integrantes: ContinuidadIntegrante[],
  estado: EstadoReparto,
): Rama[] {
  const porAfi = new Map(integrantes.map((i) => [i.afiId, i]));
  return estado.titulares
    .map((afiId, idx) => {
      const titular = porAfi.get(afiId);
      if (!titular) return null;
      return {
        titular,
        miembros: integrantes.filter((i) => estado.asignados[i.afiId] === afiId),
        esPrincipal: idx === 0,
      };
    })
    .filter((r): r is Rama => r !== null);
}

/**
 * El siguiente al que hay que preguntarle con quién queda, o `null` si ya está
 * todo repartido.
 *
 * Se respeta el orden en que vino el grupo de Binary para que la secuencia sea
 * siempre la misma: si cambiara entre respuestas, el operador vería a la gente
 * saltar de lugar.
 */
export function siguientePendiente(
  integrantes: ContinuidadIntegrante[],
  estado: EstadoReparto,
): ContinuidadIntegrante | null {
  const decidido = new Set([
    ...estado.titulares,
    ...Object.keys(estado.asignados),
    ...estado.bajas,
  ]);
  return integrantes.find((i) => !decidido.has(i.afiId)) ?? null;
}

/** Cuántas decisiones tiene el reparto en total (una por integrante del grupo). */
export const totalDecisiones = (integrantes: ContinuidadIntegrante[]): number => integrantes.length;

/** Cuántas van tomadas. Darse de baja también es una decisión. */
export function decisionesTomadas(estado: EstadoReparto): number {
  return estado.titulares.length + Object.keys(estado.asignados).length + estado.bajas.length;
}

/**
 * Saca a alguien de cualquier decisión anterior.
 *
 * Pasa al deshacer y volver a contestar distinto —primero "con la cónyuge",
 * después "que arme el suyo"—. El server rechaza el reparto entero si alguien
 * figura en dos lados a la vez.
 */
function sinDecidir(estado: EstadoReparto, afiId: string): EstadoReparto {
  return {
    titulares: estado.titulares.filter((id) => id !== afiId),
    asignados: Object.fromEntries(
      Object.entries(estado.asignados).filter(([id]) => id !== afiId),
    ),
    bajas: estado.bajas.filter((id) => id !== afiId),
  };
}

/** Deja a alguien a cargo de una rama nueva (la primera es el grupo principal). */
export function abrirRama(estado: EstadoReparto, afiId: string): EstadoReparto {
  if (estado.titulares.includes(afiId)) return estado;
  const limpio = sinDecidir(estado, afiId);
  return { ...limpio, titulares: [...limpio.titulares, afiId] };
}

/** Manda a alguien a la rama de `titularAfiId`. */
export function asignar(
  estado: EstadoReparto,
  afiId: string,
  titularAfiId: string,
): EstadoReparto {
  if (estado.titulares.includes(afiId)) return estado;
  const limpio = sinDecidir(estado, afiId);
  return { ...limpio, asignados: { ...limpio.asignados, [afiId]: titularAfiId } };
}

/** No sigue con nadie: se da de baja junto con el titular. */
export function darDeBaja(estado: EstadoReparto, afiId: string): EstadoReparto {
  if (estado.titulares.includes(afiId)) return estado;
  const limpio = sinDecidir(estado, afiId);
  return { ...limpio, bajas: [...limpio.bajas, afiId] };
}

/**
 * ¿Puede quedar con este titular?
 *
 * Sale de `puedeSeguirA`, que calcula el server con la MISMA función que después
 * valida el reparto. La pantalla no repite la regla: si lo hiciera, tarde o
 * temprano diría una cosa y el server otra.
 */
export function puedeQuedarCon(i: ContinuidadIntegrante, titularAfiId: string): boolean {
  return (i.puedeSeguirA ?? []).includes(titularAfiId);
}

/** ¿Quedó todo el grupo repartido? */
export function repartoCompleto(
  integrantes: ContinuidadIntegrante[],
  estado: EstadoReparto,
): boolean {
  return estado.titulares.length > 0 && siguientePendiente(integrantes, estado) === null;
}

/**
 * El payload que espera el server.
 *
 * El orden importa: la primera rama es el grupo principal, la que conserva el
 * legajo original y de la que cuelga el saliente.
 */
export function construirGrupos(estado: EstadoReparto): GrupoRearmado[] {
  return estado.titulares.map((titularAfiId) => ({
    titularAfiId,
    integrantesAfiIds: Object.entries(estado.asignados)
      .filter(([, t]) => t === titularAfiId)
      .map(([afiId]) => afiId),
  }));
}

/**
 * Lo que va a pasar cuando se confirme, en una frase.
 *
 * Es lo último que lee el operador antes de apretar, y el rearmado no se puede
 * deshacer solo: son dos solicitudes distintas en Binary.
 */
export function resumenReparto(
  ramasDelReparto: Rama[],
  nombreSaliente: string,
  cuantasBajas = 0,
): string {
  if (ramasDelReparto.length === 0) return '';
  const [principal, ...otras] = ramasDelReparto;

  const cuantos = principal.miembros.length;
  let acompanan = 'sin nadie más';
  if (cuantos === 1) acompanan = 'con 1 integrante más';
  else if (cuantos > 1) acompanan = `con ${cuantos} integrantes más`;

  const quien = principal.titular.parentescoLabel.toLowerCase();
  const cabeza = `Queda a cargo del legajo ${quien} (${acompanan}).`;

  let extra = '';
  if (otras.length > 0) {
    const cuantosLegajos = otras.length === 1 ? 'otro legajo' : `otros ${otras.length} legajos`;
    const para = otras.map((r) => r.titular.parentescoLabel.toLowerCase()).join(' y ');
    extra = ` Se abren ${cuantosLegajos} para ${para}.`;
  }

  // Los que se van tienen que estar en la frase: es gente que pierde la
  // cobertura, y el resumen es lo último que se lee antes de confirmar.
  let bajas = '';
  if (cuantasBajas === 1) bajas = ' 1 integrante se da de baja.';
  else if (cuantasBajas > 1) bajas = ` ${cuantasBajas} integrantes se dan de baja.`;

  return `${cabeza}${extra}${bajas} ${nombreSaliente} se da de baja.`;
}
